import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  IconButton,
  Checkbox,
  ListItemText,
  FormControl,
  Select,
  Chip,
  List,
  ListItem,
  Paper,
  SxProps,
  Typography,
  DialogContentText,
  FormHelperText,
  Grid,
  InputAdornment,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CommDataGrid from '../components/CommDataGrid';

import { GridColDef } from '@mui/x-data-grid';
import {
  addEventTag,
  deleteEvent,
  updateEvent,
  getAllTags,
  getEventUsers,
  getEventsGrid,
  getEventById,
} from '../api/requests/events';
import { getLiteUsers } from '../api/requests/users';
import CreateTagPanel from './CreateTag';
import { useAlert } from '../context/AlertContext';
import CommonDialog from '../components/common/CommonDialog';
import { menuItemStyles, PaginationType } from './Groups';
import { formatFullDateTime } from '../utils/utils';
import { useAuth } from '../context/AuthContext';
import { colors } from '../utils/constants';
import AddIcon from '../../src/assets/AddIcon.svg';
import CalendarIcon from '../../src/assets/CalendarIcon.svg';
import ManageTagsIcon from '../../src/assets/ManageTagsIcon.svg';
import ActiveTickIcon from '../../src/assets/ActiveTickIcon.svg';
import PastEventIcon from '../../src/assets/PastEventIcon.svg';
import UpcomingEventIcon from '../../src/assets/UpcomingEventIcon.svg';
import { containedButtonWithIconSx } from './Users';

interface Event {
  id: number;
  name: string;
  date: string;
  status: string;
  members: number;
  tags: string[];
  progress: number;
  startTime: any;
  endTime: any;
  description: string;
  usersToAssign: number[];
  tagsToAssign: number[];
}

interface User {
  id: number;
  name: string;
}

interface Tags {
  eventTagId: number;
  tagName: string;
  color: string;
  usageCount: number;
  description: string;
}

interface FormErrors {
  eventName: string;
  description: string;
  startTime: string;
  endTime: string;
  usersToAssign: string;
  tagsToAssign: string;
}

const EventsPage: React.FC = () => {
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [editEventId, setEditEventId] = useState<number | null>(null);
  const [mode, setMode] = useState<string | null>('');
  const [users, setUsers] = useState<User[]>([]);
  const [tags, setTags] = useState<Tags[]>([]);
  const [selectedRow, setSelectedRow] = useState<Event | null>(null);
  const [viewMembersList, setViewMembersList] = useState<string[]>([]);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [hoveredTags, setHoveredTags] = useState<
    { tagName: string; colour: string }[]
  >([]);
  const [hoverPosition, setHoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [newEvent, setNewEvent] = useState({
    eventName: '',
    description: '',
    startTime: '',
    endTime: '',
    usersToAssign: [] as number[],
    tagsToAssign: [] as number[],
  });
  const [errors, setErrors] = useState<FormErrors>({
    eventName: '',
    description: '',
    startTime: '',
    endTime: '',
    usersToAssign: '',
    tagsToAssign: '',
  });
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 10,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getEventsGrid({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        q: searchText.trim(),
      });
      if (response && response.data && response.data.eventsGridRowDtos) {
        const transformedEvents: Event[] = response.data.eventsGridRowDtos.map(
          (event: any) => {
            return {
              id: event.eventId,
              name: event.eventName,
              date: formatFullDateTime(event.startDate),
              status: event.status,
              members: event.membersCount,
              tags: event.eventTags.map((tag: any) => tag.tagName),
              progress: 0,
              startTime: event.startDate,
              endTime: null,
              description: '',
              usersToAssign: [],
              tagsToAssign: event.eventTags,
            };
          }
        );
        setEvents(transformedEvents);
        setTotalRowsCount(response.data.totalCount);
      } else {
        setEvents([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      showAlert('Error fetching events.', 'error');
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText, showAlert]);

  const fetchUsers = async () => {
    try {
      const response = await getLiteUsers();
      if (response) {
        const usersWithMappedFields = response.map((user: any) => ({
          name: user.fullName,
          id: user.userId,
        }));
        setUsers(usersWithMappedFields);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await getAllTags();
      if (response.status === 200) {
        const tagsWithMappedFields = response.data.map((tag: any) => ({
          tagName: tag.tagName,
          eventTagId: tag.eventTagId,
          color: tag.colour,
          usageCount: tag.usageCount,
          description: tag.description,
        }));
        setTags(tagsWithMappedFields);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      showAlert('Error fetching tags.', 'error');
    }
  };

  const fetchEventDetailsById = useCallback(
    async (eventId: number) => {
      try {
        const response = await getEventUsers(eventId.toString());
        if (response?.data) {
          const members =
            response.data?.map((user: any) => user.fullName) ?? [];
          setViewMembersList(members);
        } else {
          setViewMembersList([]);
        }
      } catch (error) {
        console.error('Error fetching event users:', error);
        showAlert('Error fetching event users.', 'error');
      }
    },
    [showAlert]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {
      eventName: '',
      description: '',
      startTime: '',
      endTime: '',
      usersToAssign: '',
      tagsToAssign: '',
    };
    let isValid = true;

    if (!newEvent.eventName.trim()) {
      newErrors.eventName = 'Event name is required';
      isValid = false;
    } else if (newEvent.eventName.length > 100) {
      newErrors.eventName = 'Event name must be 100 characters or less';
      isValid = false;
    }

    if (!newEvent.description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    } else if (newEvent.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
      isValid = false;
    }

    if (!newEvent.startTime) {
      newErrors.startTime = 'Start date is required';
      isValid = false;
    } else {
      const startDate = new Date(newEvent.startTime);
      const now = new Date();
      if (isNaN(startDate.getTime())) {
        newErrors.startTime = 'Invalid start date';
        isValid = false;
      } else if (startDate < now) {
        newErrors.startTime = 'Start date cannot be in the past';
        isValid = false;
      }
    }

    if (!newEvent.endTime) {
      newErrors.endTime = 'End date is required';
      isValid = false;
    } else {
      const endDate = new Date(newEvent.endTime);
      const startDate = new Date(newEvent.startTime);
      if (isNaN(endDate.getTime())) {
        newErrors.endTime = 'Invalid end date';
        isValid = false;
      } else if (newEvent.startTime && endDate <= startDate) {
        newErrors.endTime = 'End date must be after start date time';
        isValid = false;
      }
    }

    if (newEvent.usersToAssign.length === 0) {
      newErrors.usersToAssign = 'At least one user must be selected';
      isValid = false;
    }

    if (newEvent.tagsToAssign.length === 0) {
      newErrors.tagsToAssign = 'At least one tag must be selected';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }, [newEvent]);

  const handleChange = useCallback((event: any) => {
    const value = event.target.value;
    setNewEvent((prev) => ({ ...prev, usersToAssign: value }));
    setErrors((prev) => ({ ...prev, usersToAssign: '' }));
  }, []);

  const handleTagsChange = useCallback((event: any) => {
    const value = event.target.value;
    const parsedValue =
      typeof value === 'string'
        ? value.split(',').map(Number)
        : value.map(Number);
    setNewEvent((prev) => ({ ...prev, tagsToAssign: parsedValue }));
    setErrors((prev) => ({ ...prev, tagsToAssign: '' }));
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setNewEvent((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
    },
    []
  );

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, row: Event) => {
      setMenuAnchor(event.currentTarget);
      setSelectedRow(row);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const openDeleteDialog = useCallback(() => {
    setDeleteDialog(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog(false);
  }, []);

  const handleViewMembers = useCallback(() => {
    if (selectedRow) {
      setEditEventId(selectedRow.id);
      setMode('viewMembers');
    }
    handleMenuClose();
  }, [selectedRow, handleMenuClose]);

  const handleEdit = useCallback(async () => {
    if (!selectedRow?.id) {
      showAlert('No event selected for editing.', 'error');
      return;
    }
    try {
      const response = await getEventById(selectedRow.id.toString());
      if (response?.status === 200 && response.data) {
        const event = response.data;
        setEditEventId(event.eventId);
        setNewEvent({
          eventName: event.eventName || '',
          description: event.description || '',
          startTime: event.startTime || '',
          endTime: event.endTime || '',
          usersToAssign: event.usersToAssign || [],
          tagsToAssign: event.tagsToAssign || [],
        });
        setMode('Edit');
        setErrors({
          eventName: '',
          description: '',
          startTime: '',
          endTime: '',
          usersToAssign: '',
          tagsToAssign: '',
        });
      } else {
        console.error('Failed to fetch event:', response);
        showAlert('Failed to load event details.', 'error');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      showAlert('Error loading event details.', 'error');
    }
    handleMenuClose();
  }, [selectedRow, showAlert, handleMenuClose]);

  const handleCreateEvent = useCallback(async () => {
    if (!validateForm()) {
      showAlert('Please fill in all required fields correctly.', 'error');
      return;
    }
    try {
      let response;
      if (editEventId) {
        response = await updateEvent({
          eventId: String(editEventId),
          eventName: newEvent.eventName,
          description: newEvent.description,
          startTime: newEvent.startTime,
          endTime: newEvent.endTime,
          usersToAssign: newEvent.usersToAssign,
          tagsToAssign: newEvent.tagsToAssign,
        });
      } else {
        response = await addEventTag({
          eventName: newEvent.eventName,
          description: newEvent.description,
          startTime: newEvent.startTime,
          endTime: newEvent.endTime,
          usersToAssign: newEvent.usersToAssign,
          tagsToAssign: newEvent.tagsToAssign,
        });
      }

      if (response?.status === 200) {
        showAlert(
          editEventId
            ? 'Event Updated Successfully!'
            : 'Event Created Successfully!',
          'success'
        );
        if (!editEventId) {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }
        await fetchEvents();
        handleCloseCreateEditDialog();
      } else {
        console.error('Failed to save event:', response);
        showAlert(
          editEventId ? 'Event update failed.' : 'Event creation failed.',
          'error'
        );
      }
    } catch (error) {
      console.error('Error saving event:', error);
      showAlert('Error saving event.', 'error');
    }
  }, [editEventId, newEvent, validateForm, showAlert, fetchEvents]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleCloseCreateEditDialog = useCallback(() => {
    setMode('');
    setEditEventId(null);
    setSelectedRow(null);
    setNewEvent({
      eventName: '',
      description: '',
      startTime: '',
      endTime: '',
      usersToAssign: [],
      tagsToAssign: [],
    });
    setErrors({
      eventName: '',
      description: '',
      startTime: '',
      endTime: '',
      usersToAssign: '',
      tagsToAssign: '',
    });
    setViewMembersList([]);
  }, []);

  const handleCloseViewMembersDialog = useCallback(() => {
    setMode('');
    setEditEventId(null);
    setSelectedRow(null);
    setViewMembersList([]);
  }, []);

  const handleDialogClose = useCallback(
    (_event: {}, reason: string) => {
      if (reason !== 'backdropClick') {
        if (mode === 'viewMembers') {
          handleCloseViewMembersDialog();
        } else {
          handleCloseCreateEditDialog();
        }
      }
    },
    [mode, handleCloseViewMembersDialog, handleCloseCreateEditDialog]
  );

  const convertToDateTimeLocal = useCallback(
    (isoString: string | undefined | null) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60000);
      return localDate.toISOString().slice(0, 16);
    },
    []
  );

  const handleDelete = useCallback(async () => {
    if (!selectedRow) {
      showAlert('No event selected', 'error');
      return;
    }
    try {
      const response = await deleteEvent(selectedRow.id);
      if (response && response.status === 200) {
        setSelectedRow(null);
        closeDeleteDialog();
        showAlert('Event deleted successfully!', 'success');
        await fetchEvents();
      } else {
        showAlert('Failed to delete event', 'error');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      showAlert('Error deleting event', 'error');
    }
  }, [selectedRow, showAlert, fetchEvents, closeDeleteDialog]);

  const textFieldRows = [
    [
      {
        label: 'Event Name',
        name: 'eventName',
        value: newEvent.eventName,
        error: errors.eventName,
        type: 'text',
      },
      {
        label: 'Event Description',
        name: 'description',
        value: newEvent.description,
        error: errors.description,
        type: 'text',
      },
    ],
    [
      {
        label: 'Start Date',
        name: 'startTime',
        value: convertToDateTimeLocal(newEvent.startTime),
        error: errors.startTime,
        type: 'datetime-local',
      },
      {
        label: 'End Date',
        name: 'endTime',
        value: convertToDateTimeLocal(newEvent.endTime),
        error: errors.endTime,
        type: 'datetime-local',
      },
    ],
  ];

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        headerClassName: 'custom-header',
        disableColumnMenu: true,
        renderCell: (params) => (
          <Typography variant='body2' sx={{ display: 'contents' }}>
            {params.value || '--'}
          </Typography>
        ),
      },
      {
        field: 'members',
        headerName: 'Members',
        flex: 1,
        width: 30,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box display='flex' alignItems='center' gap={1}>
            <Typography variant='body2' sx={{ display: 'contents' }}>
              {params.value || 0} Members
            </Typography>
          </Box>
        ),
      },
      {
        field: 'date',
        headerName: 'Created Date',
        flex: 1,
        headerClassName: 'custom-header',
        disableColumnMenu: true,
        renderCell: (params) => (
          <Typography variant='body2' sx={{ display: 'contents' }}>
            {params.value || '--'}
          </Typography>
        ),
      },

      {
        field: 'tagsToAssign',
        headerName: 'Tags',
        flex: 1,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
            {params.value.slice(0, 1).map((data: any, index: number) => {
              return (
                <Chip
                  key={index}
                  label={data.tagName}
                  sx={{
                    backgroundColor: data.colour,
                    color: 'white',
                    height: '24px',
                  }}
                />
              );
            })}
            {params.value.length > 1 && (
              <Chip
                label={`+${params.value.length - 1} more`}
                sx={{ height: '24px', cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const hovered = params.value
                    .map((data: any) => {
                      return { tagName: data.tagName, colour: data.colour };
                    })
                    .filter(Boolean) as { tagName: string; colour: string }[];
                  setHoveredTags(hovered);
                  setHoverPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                }}
                onMouseLeave={() => {
                  setHoveredTags([]);
                  setHoverPosition(null);
                }}
              />
            )}
          </Box>
        ),
      },

      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box sx={statusIconStyles}>
            <img
              src={
                params.value === 'Active'
                  ? ActiveTickIcon
                  : params.value === 'Upcoming'
                  ? UpcomingEventIcon
                  : PastEventIcon
              }
              alt='status-icon'
              style={{ width: 20, height: 20, marginRight: 8 }}
            />
            <span
              style={{
                borderRadius: '15px',
                fontSize: '14px',
              }}
            >
              {params.value || ''}
            </span>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        renderCell: (params) => (
          <>
            {params.row.status !== 'Deleted' && (
              <>
                <IconButton
                  onClick={(event) => handleMenuOpen(event, params.row)}
                >
                  <MoreVertIcon sx={{ color: '#64748B' }} />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={
                    Boolean(menuAnchor) && selectedRow?.id === params.row.id
                  }
                  onClose={handleMenuClose}
                  key={params.id}
                  sx={menuItemStyles}
                >
                  {user?.permissions?.some(
                    (p) =>
                      p.module === 'EventTags' && p.actions.includes('Edit')
                  ) && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
                  {user?.permissions?.some(
                    (p) =>
                      p.module === 'EventTags' && p.actions.includes('Edit')
                  ) && (
                    <MenuItem onClick={openDeleteDialog}>Cancel Event</MenuItem>
                  )}
                  <MenuItem onClick={handleViewMembers}>View Members</MenuItem>
                </Menu>
              </>
            )}
          </>
        ),
      },
    ],
    [
      user,
      menuAnchor,
      selectedRow,
      handleMenuOpen,
      handleMenuClose,
      handleEdit,
      openDeleteDialog,
      handleViewMembers,
    ]
  );

  const renderButtons = useMemo(
    () => () =>
      (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant='outlined'
            sx={tagIconStyles}
            onClick={() => setMode('manageTags')}
          >
            <img src={ManageTagsIcon} />
          </Button>
          {user?.permissions?.some(
            (p) => p.module === 'EventTags' && p.actions.includes('Create')
          ) && (
            <Button
              variant='contained'
              onClick={() => {
                setMode('createEvent');
              }}
              sx={{ ...containedButtonWithIconSx, padding: '9px' }}
            >
              <img src={AddIcon} /> Create Event{' '}
            </Button>
          )}
        </Box>
      ),
    [user]
  );

  useEffect(() => {
    fetchEvents();
  }, [paginationModel, searchText]);

  useEffect(() => {
    fetchUsers();
    fetchTags();
  }, []);

  useEffect(() => {
    if (editEventId && mode === 'viewMembers') {
      fetchEventDetailsById(editEventId);
    }
  }, [editEventId, mode, fetchEventDetailsById]);

  return (
    <Box>
      <Box sx={{ position: 'relative' }}>
        <CommDataGrid
          title='Event Management'
          description='View and manage all events in the system'
          rows={events}
          columns={columns}
          section='Events'
          renderButtons={renderButtons}
          setPaginationModel={setPaginationModel}
          paginationModel={paginationModel}
          totalRowsCount={totalRowsCount}
          searchText={searchText}
          handleSearch={handleSearch}
          loading={loading}
        />
        {hoveredTags.length > 0 && hoverPosition && (
          <Paper
            elevation={4}
            sx={{
              ...paperStyles,
              top: `${hoverPosition.y - 60}px`,
              left: `${hoverPosition.x}px`,
            }}
            onMouseLeave={() => {
              setHoveredTags([]);
              setHoverPosition(null);
            }}
          >
            {hoveredTags.map((tag, index) => (
              <Chip
                key={index}
                label={tag.tagName}
                sx={{
                  backgroundColor: tag.colour,
                  color: 'white',
                  height: '24px',
                }}
              />
            ))}
          </Paper>
        )}
      </Box>
      {/* View Members Dialog */}
      {mode === 'viewMembers' && (
        <CommonDialog title='View Members' hideActionButtons cancelButton={handleCloseViewMembersDialog}>
          <DialogContent sx={{ padding: '2px 24px' }}>
            {viewMembersList.length > 0 ? (
              <List>
                {viewMembersList.map((member: string, index: number) => (
                  <ListItem
                    key={index}
                    sx={{ '&.MuiListItem-root': { padding: 0 } }}
                  >
                    <Box display='flex' alignItems='center' width='100%'>
                      <ListItemText
                        primary={member}
                        sx={{ whiteSpace: 'wrap', fontSize: '12px' }}
                      />
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No Members found</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseViewMembersDialog}
              sx={{
                ...cancelButtonStyles,
                color: colors.indigoBlueColor,
                mr: 1,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </CommonDialog>
      )}
      {/* Create/Edit/Manage Tags Dialog */}
      {mode && mode !== 'viewMembers' && (
        <Dialog
          open={!!mode}
          onClose={handleDialogClose}
          fullWidth
          sx={dialogStyles}
          TransitionProps={{ timeout: 0 }}
        >
          {mode === 'manageTags' ? (
            <CreateTagPanel
              setMode={setMode}
              fetchEvents={fetchEvents}
              RefreshTags={fetchTags}
            />
          ) : (
            <>
              <>
                <CommonDialog
                  title={mode === 'Edit' ? 'Edit Event' : 'Create New Event'}
                  hideActionButtons
                  cancelButton={handleCloseCreateEditDialog}
                >
                  <DialogContent>
                    <Grid container spacing={2}>
                      {textFieldRows.map((row, rowIndex) =>
                        row.map((field, index) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            key={`${rowIndex}-${field.name}`}
                          >
                            <Typography sx={{ fontSize: '14px' }}>
                              {field.label}
                              <Box component='span' sx={asteriskStyle}>
                                {' '}
                                *
                              </Box>
                            </Typography>
                            <TextField
                              margin='dense'
                              name={field.name}
                              type={field.type}
                              fullWidth
                              inputRef={(el) => {
                                if (el) inputRefs.current[field.name] = el;
                              }}
                              value={field.value}
                              onChange={handleInputChange}
                              error={!!field.error}
                              helperText={field.error}
                              slotProps={{
                                input: {
                                  sx: labelStyles,
                                  endAdornment: field.type ===
                                    'datetime-local' && (
                                    <InputAdornment position='end'>
                                      <IconButton
                                        onClick={() =>
                                          inputRefs.current[
                                            field.name
                                          ]?.showPicker?.()
                                        }
                                        sx={{
                                          p: 0,
                                        }}
                                      >
                                        <Box
                                          component='img'
                                          src={CalendarIcon}
                                          alt='calendar'
                                          width={30}
                                        />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              }}
                              InputLabelProps={
                                field.type === 'datetime-local'
                                  ? { shrink: true }
                                  : undefined
                              }
                              sx={
                                field.type === 'datetime-local'
                                  ? {
                                      '& input::-webkit-calendar-picker-indicator':
                                        {
                                          display: 'none', // hide default icon
                                        },
                                    }
                                  : undefined
                              }
                            />
                          </Grid>
                        ))
                      )}

                      {/* Select Users */}
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontSize: '14px' }}>
                          Select Users{' '}
                          <Box component='span' sx={asteriskStyle}>
                            *
                          </Box>
                        </Typography>
                        <FormControl
                          fullWidth
                          sx={{ mt: 1 }}
                          error={!!errors.usersToAssign}
                        >
                          <Select
                            slotProps={{
                              input: {
                                sx: {
                                  padding: '8px',
                                  borderRadius: '10px',
                                  fontSize: '12px',
                                },
                              },
                            }}
                            sx={{ height: '48px', borderRadius: '8px' }}
                            multiple
                            value={newEvent.usersToAssign}
                            onChange={handleChange}
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  flexWrap: 'wrap',
                                  alignItems: 'center',
                                }}
                              >
                                {selected.slice(0, 2).map((id) => {
                                  const option = users.find(
                                    (opt) => opt.id === id
                                  );
                                  return option ? (
                                    <Chip
                                      key={id}
                                      label={option.name}
                                      sx={{ fontSize: 10 }}
                                    />
                                  ) : null;
                                })}
                                {selected.length > 2 && (
                                  <Chip
                                    label={`+${selected.length - 2} more`}
                                    sx={{ fontSize: 10 }}
                                  />
                                )}
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: { style: { maxHeight: 200 } },
                            }}
                          >
                            {users.map(({ id, name }) => (
                              <MenuItem key={id} value={id}>
                                <Checkbox
                                  checked={newEvent.usersToAssign.includes(id)}
                                />
                                <ListItemText primary={name} />
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.usersToAssign && (
                            <FormHelperText>
                              {errors.usersToAssign}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      {/* Select Tags */}
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontSize: '14px' }}>
                          Select Tags{' '}
                          <Box component='span' sx={asteriskStyle}>
                            *
                          </Box>
                        </Typography>
                        <FormControl
                          fullWidth
                          sx={{ mt: 1 }}
                          error={!!errors.tagsToAssign}
                        >
                          <Select
                            multiple
                            value={newEvent.tagsToAssign}
                            onChange={handleTagsChange}
                            slotProps={{
                              input: {
                                sx: {
                                  padding: '6px',
                                  fontSize: '12px',
                                },
                              },
                            }}
                            sx={{ height: '48px', borderRadius: '8px' }}
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  flexWrap: 'wrap',
                                  alignItems: 'center',
                                }}
                              >
                                {selected.slice(0, 2).map((id) => {
                                  const option = tags.find(
                                    (opt) => opt.eventTagId === id
                                  );
                                  return option ? (
                                    <Chip
                                      key={id}
                                      label={option.tagName}
                                      sx={{ fontSize: 10 }}
                                    />
                                  ) : null;
                                })}
                                {selected.length > 2 && (
                                  <Chip
                                    label={`+${selected.length - 2} more`}
                                    sx={{ fontSize: 10 }}
                                  />
                                )}
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: { style: { maxHeight: 200 } },
                            }}
                          >
                            {tags.map(({ eventTagId, tagName }) => (
                              <MenuItem key={eventTagId} value={eventTagId}>
                                <Checkbox
                                  checked={newEvent.tagsToAssign.includes(
                                    eventTagId
                                  )}
                                />
                                <ListItemText primary={tagName} />
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.tagsToAssign && (
                            <FormHelperText>
                              {errors.tagsToAssign}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </DialogContent>

                  <DialogActions sx={{ p: 3 }}>
                    <Button
                      onClick={handleCloseCreateEditDialog}
                      sx={cancelButtonStyles}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      onClick={handleCreateEvent}
                      sx={createButtonStyles}
                    >
                      {mode === 'Edit' ? 'Update' : 'Create'}
                    </Button>
                  </DialogActions>
                </CommonDialog>
              </>
            </>
          )}
        </Dialog>
      )}
      {deleteDialog && (
        <CommonDialog
          title='Delete Event'
          cancelButton={closeDeleteDialog}
          actionButton={handleDelete}
          actionButtonTitle='Yes'
          cancelButtonTitle='Cancel'
        >
          <DialogContentText>
            <Typography
              fontSize='13px'
              sx={{ color: '#000000', paddingBottom: 1, pl: 2 }}
            >
              Are you sure you want to delete this event?
            </Typography>
            <Typography fontSize='12px' pl={2}>
              The event will be deleted if you click on 'Yes'.
            </Typography>
          </DialogContentText>
        </CommonDialog>
      )}
    </Box>
  );
};

export default EventsPage;

const paperStyles: SxProps = {
  position: 'fixed',
  transform: 'translateX(-50%)',
  zIndex: 1500,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  p: 1,
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 6,
  maxWidth: '80vw',
};

const asteriskStyle: SxProps = {
  color: 'red',
};

const dialogStyles: SxProps<Theme> = (theme) => ({
  p: theme.spacing(1, 2, 1, 2),
  '.MuiDialog-paper': {
    borderRadius: '4px',
    minWidth: 400,
  },
});

const labelStyles: SxProps = {
  fontSize: '12px',
  padding: '6px',
  borderRadius: '8px',
  height: '48px',
  '&:-webkit-autofill': {
    boxShadow: '0 0 0 1000px white inset',
    WebkitTextFillColor: 'black',
    borderRadius: '8px',
  },
};

const cancelButtonStyles: SxProps = {
  borderRadius: '8px',
  textTransform: 'inherit',
  color: '#ABABAB',
  border: '1px solid #ABABAB',
  pl: 3,
  pr: 3,
};

const createButtonStyles: SxProps = {
  ml: 2,
  pl: 3,
  pr: 3,
  borderRadius: '8px',
  textTransform: 'inherit',
  backgroundColor: colors.indigoBlueColor,
};

const tagIconStyles: SxProps = {
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
  backgroundColor: colors.white,
  borderRadius: '8px',
  height: '45px',
};

const createEventStyles: SxProps = {
  display: 'flex',
  gap: 1,
  borderRadius: '8px',
  backgroundColor: colors.indigoBlueColor,
  color: colors.white,
  textTransform: 'inherit',
  ml: 1,
  padding: '8px 12px',
};

const statusIconStyles: SxProps = {
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  width: '100%',
  borderRadius: '15px',
};
